import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setView from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetView";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { ProductListViewType } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Grid from "@tIntermatic/Icons/Grid";
import List from "@tIntermatic/Icons/List";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    loaded: !!getProductListDataView(state).value,
    view: state.pages.productList.view,
    productsDataView: getProductListDataView(state),
    searchQuery: state.pages.productList.parameter?.search,
    searchWithinQuery: state.pages.productList?.filteredApiParameter?.searchWithin,
});

const mapDispatchToProps = {
    setView,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListViewSelectStyles {
    wrapper?: InjectableCss;
    listViewClickable?: ClickablePresentationProps;
    listViewIconSelected?: IconPresentationProps;
    listViewIconUnSelected?: IconPresentationProps;
    gridViewClickable?: ClickablePresentationProps;
    gridViewIconSelected?: IconPresentationProps;
    gridViewIconUnSelected?: IconPresentationProps;
}

export const viewSelectStyles: ProductListViewSelectStyles = {
    wrapper: {
        css: css`
            display: flex;
            padding-top: 5px;
            float: right;
            @media all and (max-width: 991px) and (min-width: 0px) {
                display: none;
            }
        `,
    },
    listViewClickable: {
        css: css`
            margin-right: 12px;
        `,
    },
    gridViewClickable: {
        css: css`
            margin-right: 12px;
        `,
    },
    listViewIconSelected: {
        src: List,
        size: 15,
    },
    listViewIconUnSelected: {
        src: List,
        size: 15,
        css: css`
            > svg {
                > g {
                    > g {
                        fill: #cccccc;
                    }
                }
            }
        `,
    },
    gridViewIconSelected: {
        src: Grid,
        size: 15,
    },
    gridViewIconUnSelected: {
        src: Grid,
        size: 15,
        css: css`
            > svg {
                > g {
                    > g {
                        fill: #cccccc;
                    }
                }
            }
        `,
    },
};

const styles = viewSelectStyles;

class ProductListViewSelect extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        const view = getCookie("ProductList-View");
        if (view && this.props.view !== view) {
            this.props.setView({ view: view as ProductListViewType });
        }
    }

    render() {
        const { loaded, view, setView, productsDataView, searchQuery, searchWithinQuery } = this.props;

        if (!loaded) {
            return null;
        }

        const clickListViewHandler = () => {
            changeView("List");
        };

        const clickGridViewHandler = () => {
            changeView("Grid");
        };

        const changeView = (view: ProductListViewType) => {
            setCookie("ProductList-View", view);
            setView({ view });
        };

        const displayView = () => {
            return (
                <StyledWrapper {...styles.wrapper}>
                    <Clickable
                        data-test-selector="viewSelectGrid"
                        {...styles.gridViewClickable}
                        onClick={clickGridViewHandler}
                    >
                        <Icon
                            {...(view === "Grid"
                                ? { ...styles.gridViewIconSelected }
                                : { ...styles.gridViewIconUnSelected })}
                        />
                    </Clickable>
                    <Clickable
                        data-test-selector="viewSelectList"
                        {...styles.listViewClickable}
                        onClick={clickListViewHandler}
                    >
                        <Icon
                            {...(view === "List"
                                ? { ...styles.listViewIconSelected }
                                : { ...styles.listViewIconUnSelected })}
                        />
                    </Clickable>
                </StyledWrapper>
            );
        };

        const conditionalView = () => {
            if (searchWithinQuery) {
                return displayView();
            }
            if (searchQuery && productsDataView.value?.length === 0) {
                return null;
            }
            return displayView();
        };

        return conditionalView();
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListViewSelect),
    definition: {
        group: "Product List",
        displayName: "View Select",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
